import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Alert,
  Stack,
  Card,
  useTheme,
  alpha,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Chip,
} from '@mui/material';
import AuthenticatedAvatar from '../../../components/AuthenticatedAvatar';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  PhotoCamera as PhotoCameraIcon,
  Upload as UploadIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Close as CloseIcon,
  Add as AddIcon,
  CameraAlt as CameraAltIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  School as SchoolIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Info as InfoIcon,
  PhotoLibrary as PhotoLibraryIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

interface ProfilePhoto {
  url: string;
  is_default: boolean;
  uploaded_at: string;
}

interface Province {
  _id: string;
  code: number;
  name: Array<{ code: string; name: string }>;
  en_name: string;
  is_active: boolean;
}

interface District {
  _id: string;
  code: number;
  name: Array<{ code: string; name: string }>;
  en_name: string;
  province_code: number;
  is_active: boolean;
}

interface SubDistrict {
  _id: string;
  code: number;
  name: Array<{ code: string; name: string }>;
  en_name: string;
  district_code: number;
  province_code: number;
  zipcode?: string;
  is_active: boolean;
}

interface Degree {
  _id: string;
  code: string;
  name_th: string;
  name_en: string;
  level: number;
  is_active: boolean;
}

interface Faculty {
  _id: string;
  code: string;
  name_th: string;
  name_en: string;
  abbreviation_th?: string;
  abbreviation_en?: string;
  is_active: boolean;
}

interface Alumni {
  _id: string;
  student_id: string;
  citizen_id: string;
  title: string;
  nickname?: string;
  first_name_th: string;
  last_name_th: string;
  first_name_en: string;
  last_name_en: string;
  current_first_name_th?: string;
  current_last_name_th?: string;
  current_first_name_en?: string;
  current_last_name_en?: string;
  birth_date: string;
  gender: string;
  professional_license_number?: string;
  alumni_type: string;
  batch?: string;
  ideals?: string[];
  approval_status: string;
  profile_photos?: ProfilePhoto[];
  education: {
    degree: string;
    faculty: string;
    graduation_year: number;
    gpa?: number;
  };
  contact: {
    email: string;
    phone: string;
    line_id?: string;
    facebook?: string;
  };
  address: {
    address_line: string;
    sub_district_code: number;
    sub_district: string;
    district_code: number;
    district: string;
    province_code: number;
    province: string;
    postal_code: string;
    country: string;
  };
  work_address?: {
    address_line: string;
    sub_district_code?: number;
    sub_district: string;
    district_code?: number;
    district: string;
    province_code?: number;
    province: string;
    postal_code: string;
    country: string;
  };
  current_occupation?: string;
  user_id: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`edit-tabpanel-${index}`}
      aria-labelledby={`edit-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function AlumniEdit() {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [alumni, setAlumni] = useState<Alumni | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Photo management
  const [profilePhotos, setProfilePhotos] = useState<ProfilePhoto[]>([]);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);

  // Geolocation state
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [homeDistricts, setHomeDistricts] = useState<District[]>([]);
  const [homeSubDistricts, setHomeSubDistricts] = useState<SubDistrict[]>([]);
  const [workDistricts, setWorkDistricts] = useState<District[]>([]);
  const [workSubDistricts, setWorkSubDistricts] = useState<SubDistrict[]>([]);
  const [geoLoading, setGeoLoading] = useState(false);

  // Education master data
  const [degrees, setDegrees] = useState<Degree[]>([]);
  const [faculties, setFaculties] = useState<Faculty[]>([]);


  const [formData, setFormData] = useState({
    student_id: '',
    citizen_id: '',
    title: 'นาย',
    nickname: '',
    first_name_th: '',
    last_name_th: '',
    first_name_en: '',
    last_name_en: '',
    current_first_name_th: '',
    current_last_name_th: '',
    current_first_name_en: '',
    current_last_name_en: '',
    birth_date: '',
    gender: 'male',
    professional_license_number: '',
    alumni_type: 'regular',
    batch: '',
    ideals: '',
    degree: '',
    faculty: '',
    graduation_year: new Date().getFullYear(),
    gpa: '',
    email: '',
    phone: '',
    line_id: '',
    facebook: '',
    address_line: '',
    province_code: '',
    district_code: '',
    sub_district_code: '',
    sub_district: '',
    district: '',
    province: '',
    postal_code: '',
    country: 'Thailand',
    work_address_line: '',
    work_province_code: '',
    work_district_code: '',
    work_sub_district_code: '',
    work_sub_district: '',
    work_district: '',
    work_province: '',
    work_postal_code: '',
    work_country: 'Thailand',
    current_occupation: '',
  });

  useEffect(() => {
    fetchAlumniData();
    fetchProvinces();
    fetchDegrees();
    fetchFaculties();
  }, [id]);

  const fetchAlumniData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/alumni/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const alumniData = response.data;
      setAlumni(alumniData);
      setProfilePhotos(alumniData.profile_photos || []);

      // Populate form with existing data
      setFormData({
        student_id: alumniData.student_id,
        citizen_id: alumniData.citizen_id,
        title: alumniData.title,
        nickname: alumniData.nickname || '',
        first_name_th: alumniData.first_name_th,
        last_name_th: alumniData.last_name_th,
        first_name_en: alumniData.first_name_en,
        last_name_en: alumniData.last_name_en,
        current_first_name_th: alumniData.current_first_name_th || '',
        current_last_name_th: alumniData.current_last_name_th || '',
        current_first_name_en: alumniData.current_first_name_en || '',
        current_last_name_en: alumniData.current_last_name_en || '',
        birth_date: alumniData.birth_date?.split('T')[0] || '',
        gender: alumniData.gender,
        professional_license_number: alumniData.professional_license_number || '',
        alumni_type: alumniData.alumni_type,
        batch: alumniData.batch || '',
        ideals: alumniData.ideals ? alumniData.ideals.join(', ') : '',
        degree: alumniData.education.degree,
        faculty: alumniData.education.faculty,
        graduation_year: alumniData.education.graduation_year,
        gpa: alumniData.education.gpa?.toString() || '',
        email: alumniData.contact.email,
        phone: alumniData.contact.phone,
        line_id: alumniData.contact.line_id || '',
        facebook: alumniData.contact.facebook || '',
        address_line: alumniData.address.address_line,
        province_code: alumniData.address.province_code?.toString() || '',
        district_code: alumniData.address.district_code?.toString() || '',
        sub_district_code: alumniData.address.sub_district_code?.toString() || '',
        sub_district: alumniData.address.sub_district,
        district: alumniData.address.district,
        province: alumniData.address.province,
        postal_code: alumniData.address.postal_code,
        country: alumniData.address.country,
        work_address_line: alumniData.work_address?.address_line || '',
        work_province_code: alumniData.work_address?.province_code?.toString() || '',
        work_district_code: alumniData.work_address?.district_code?.toString() || '',
        work_sub_district_code: alumniData.work_address?.sub_district_code?.toString() || '',
        work_sub_district: alumniData.work_address?.sub_district || '',
        work_district: alumniData.work_address?.district || '',
        work_province: alumniData.work_address?.province || '',
        work_postal_code: alumniData.work_address?.postal_code || '',
        work_country: alumniData.work_address?.country || 'Thailand',
        current_occupation: alumniData.current_occupation || '',
      });

      // Fetch related dropdown data
      if (alumniData.address.province_code) {
        await fetchDistricts(alumniData.address.province_code, false);
      }
      if (alumniData.address.district_code) {
        await fetchSubDistricts(alumniData.address.district_code, false);
      }
      if (alumniData.work_address?.province_code) {
        await fetchDistricts(alumniData.work_address.province_code, true);
      }
      if (alumniData.work_address?.district_code) {
        await fetchSubDistricts(alumniData.work_address.district_code, true);
      }

      setError(null);
    } catch (err: any) {
      console.error('Error fetching alumni:', err);
      setError(err.response?.data?.detail || 'Failed to load alumni data');
    } finally {
      setLoading(false);
    }
  };

  // Geolocation helpers
  const getThaiName = (item: Province | District | SubDistrict) => {
    return item.name?.find((n) => n.code === 'th')?.name || item.en_name;
  };

  const fetchProvinces = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/master-data/provinces`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProvinces(response.data);
    } catch (err) {
      console.error('Failed to fetch provinces:', err);
    }
  };

  const fetchDistricts = async (provinceCode: number, isWorkAddress: boolean = false) => {
    try {
      setGeoLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/master-data/districts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { province_code: provinceCode },
      });
      if (isWorkAddress) {
        setWorkDistricts(response.data);
      } else {
        setHomeDistricts(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch districts:', err);
    } finally {
      setGeoLoading(false);
    }
  };

  const fetchSubDistricts = async (districtCode: number, isWorkAddress: boolean = false) => {
    try {
      setGeoLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/master-data/sub-districts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { district_code: districtCode, limit: 5000 },
      });
      const items = response.data.items || response.data;
      if (isWorkAddress) {
        setWorkSubDistricts(items);
      } else {
        setHomeSubDistricts(items);
      }
    } catch (err) {
      console.error('Failed to fetch sub-districts:', err);
    } finally {
      setGeoLoading(false);
    }
  };

  // Education master data
  const fetchDegrees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/master-data/degrees`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { is_active: true },
      });
      setDegrees(response.data);
    } catch (err) {
      console.error('Failed to fetch degrees:', err);
    }
  };

  const fetchFaculties = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/master-data/faculties`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { is_active: true },
      });
      setFaculties(response.data);
    } catch (err) {
      console.error('Failed to fetch faculties:', err);
    }
  };



  // Photo management functions
  const handleSetDefaultPhoto = (index: number) => {
    const updatedPhotos = profilePhotos.map((photo, i) => ({
      ...photo,
      is_default: i === index,
    }));
    setProfilePhotos(updatedPhotos);
  };

  const handleRemovePhoto = (index: number) => {
    const updatedPhotos = profilePhotos.filter((_, i) => i !== index);
    // If removed photo was default and there are more photos, set first as default
    if (profilePhotos[index].is_default && updatedPhotos.length > 0) {
      updatedPhotos[0].is_default = true;
    }
    setProfilePhotos(updatedPhotos);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    await uploadPhoto(file);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
      });
      setVideoStream(stream);
      setCameraActive(true);
      setPhotoError(null);
      
      // Wait for next tick to ensure video element is rendered
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(err => {
            console.error('Error playing video:', err);
            setPhotoError('Failed to start video preview.');
          });
        }
      }, 100);
    } catch (err: any) {
      console.error('Camera access error:', err);
      setCameraActive(false);
      setPhotoError(`Failed to access camera: ${err.message || 'Please check permissions.'}`);
    }
  };

  const stopCamera = () => {
    if (videoStream) {
      videoStream.getTracks().forEach((track) => track.stop());
      setVideoStream(null);
    }
    setCameraActive(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
        await uploadPhoto(file);
        stopCamera();
        setPhotoDialogOpen(false);
      }
    }, 'image/jpeg');
  };

  const uploadPhoto = async (file: File) => {
    try {
      setUploading(true);
      setPhotoError(null);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'profiles');

      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/api/v1/upload/`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const newPhoto: ProfilePhoto = {
        url: response.data.url,
        is_default: profilePhotos.length === 0,
        uploaded_at: new Date().toISOString(),
      };

      setProfilePhotos([...profilePhotos, newPhoto]);
    } catch (err: any) {
      setPhotoError(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  // Province change handlers
  const handleProvinceChange = (value: string, isWorkAddress: boolean = false) => {
    const provinceCode = parseInt(value);
    const selectedProvince = provinces.find((p) => p.code === provinceCode);

    if (isWorkAddress) {
      setFormData({
        ...formData,
        work_province_code: value,
        work_province: selectedProvince ? getThaiName(selectedProvince) : '',
        work_district_code: '',
        work_district: '',
        work_sub_district_code: '',
        work_sub_district: '',
      });
      setWorkDistricts([]);
      setWorkSubDistricts([]);
      if (provinceCode) fetchDistricts(provinceCode, true);
    } else {
      setFormData({
        ...formData,
        province_code: value,
        province: selectedProvince ? getThaiName(selectedProvince) : '',
        district_code: '',
        district: '',
        sub_district_code: '',
        sub_district: '',
      });
      setHomeDistricts([]);
      setHomeSubDistricts([]);
      if (provinceCode) fetchDistricts(provinceCode, false);
    }
  };

  const handleDistrictChange = (value: string, isWorkAddress: boolean = false) => {
    const districtCode = parseInt(value);
    const districts = isWorkAddress ? workDistricts : homeDistricts;
    const selectedDistrict = districts.find((d) => d.code === districtCode);

    if (isWorkAddress) {
      setFormData({
        ...formData,
        work_district_code: value,
        work_district: selectedDistrict ? getThaiName(selectedDistrict) : '',
        work_sub_district_code: '',
        work_sub_district: '',
      });
      setWorkSubDistricts([]);
      if (districtCode) fetchSubDistricts(districtCode, true);
    } else {
      setFormData({
        ...formData,
        district_code: value,
        district: selectedDistrict ? getThaiName(selectedDistrict) : '',
        sub_district_code: '',
        sub_district: '',
      });
      setHomeSubDistricts([]);
      if (districtCode) fetchSubDistricts(districtCode, false);
    }
  };

  const handleSubDistrictChange = (value: string, isWorkAddress: boolean = false) => {
    const subDistrictCode = parseInt(value);
    const subDistricts = isWorkAddress ? workSubDistricts : homeSubDistricts;
    const selectedSubDistrict = subDistricts.find((sd) => sd.code === subDistrictCode);

    if (isWorkAddress) {
      setFormData({
        ...formData,
        work_sub_district_code: value,
        work_sub_district: selectedSubDistrict ? getThaiName(selectedSubDistrict) : '',
        work_postal_code: selectedSubDistrict?.zipcode || formData.work_postal_code,
      });
    } else {
      setFormData({
        ...formData,
        sub_district_code: value,
        sub_district: selectedSubDistrict ? getThaiName(selectedSubDistrict) : '',
        postal_code: selectedSubDistrict?.zipcode || formData.postal_code,
      });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate at least one default photo if photos are provided
      if (profilePhotos.length > 0 && !profilePhotos.some((p) => p.is_default)) {
        setError('Please select a default profile photo');
        setSaving(false);
        return;
      }

      const idealsArray = formData.ideals
        ? formData.ideals.split(',').map((item) => item.trim()).filter((item) => item)
        : [];

      const data = {
        student_id: formData.student_id,
        citizen_id: formData.citizen_id,
        title: formData.title,
        nickname: formData.nickname || null,
        first_name_th: formData.first_name_th,
        last_name_th: formData.last_name_th,
        first_name_en: formData.first_name_en,
        last_name_en: formData.last_name_en,
        current_first_name_th: formData.current_first_name_th || null,
        current_last_name_th: formData.current_last_name_th || null,
        current_first_name_en: formData.current_first_name_en || null,
        current_last_name_en: formData.current_last_name_en || null,
        birth_date: formData.birth_date ? `${formData.birth_date}T00:00:00Z` : null,
        gender: formData.gender,
        professional_license_number: formData.professional_license_number || null,
        alumni_type: formData.alumni_type,
        batch: formData.batch || null,
        ideals: idealsArray,
        education: {
          degree: formData.degree,
          faculty: formData.faculty,
          graduation_year: parseInt(formData.graduation_year.toString()),
          gpa: formData.gpa ? parseFloat(formData.gpa) : null,
        },
        contact: {
          email: formData.email,
          phone: formData.phone,
          line_id: formData.line_id || null,
          facebook: formData.facebook || null,
        },
        address: {
          address_line: formData.address_line,
          sub_district_code: parseInt(formData.sub_district_code),
          sub_district: formData.sub_district,
          district_code: parseInt(formData.district_code),
          district: formData.district,
          province_code: parseInt(formData.province_code),
          province: formData.province,
          postal_code: formData.postal_code,
          country: formData.country,
        },
        work_address: formData.work_address_line
          ? {
              address_line: formData.work_address_line,
              sub_district_code: formData.work_sub_district_code
                ? parseInt(formData.work_sub_district_code)
                : null,
              sub_district: formData.work_sub_district,
              district_code: formData.work_district_code ? parseInt(formData.work_district_code) : null,
              district: formData.work_district,
              province_code: formData.work_province_code ? parseInt(formData.work_province_code) : null,
              province: formData.work_province,
              postal_code: formData.work_postal_code,
              country: formData.work_country,
            }
          : null,
        training: [],
        work_history: [],
        current_occupation: formData.current_occupation || null,
        profile_photos: profilePhotos,
      };

      console.log('Sending alumni update data:', data);

      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/v1/alumni/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess(true);
      setTimeout(() => {
        navigate(`/alumni/${id}`);
      }, 1500);
    } catch (err: any) {
      console.error('Error saving alumni:', err);
      console.error('Error response:', err.response?.data);
      
      // Handle Pydantic validation errors (422)
      let errorMessage = 'Failed to save changes';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation error format
          errorMessage = detail.map((e: any) => {
            const field = e.loc?.join('.') || 'field';
            return `${field}: ${e.msg}`;
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !alumni) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/alumni')}>
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={() => navigate(`/alumni/${id}`)} sx={{ bgcolor: 'background.paper' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight="bold">
            {t('alumni.edit')}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={() => navigate(`/alumni/${id}`)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('alumni.messages.saveSuccess')}
        </Alert>
      )}

      {/* Form Card */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, val) => setTabValue(val)} variant="scrollable" scrollButtons="auto">
            <Tab label={t('alumni.tabs.profilePhotos')} icon={<PhotoLibraryIcon />} iconPosition="start" />
            <Tab label={t('alumni.tabs.personalInfo')} icon={<PersonIcon />} iconPosition="start" />
            <Tab label={t('alumni.tabs.education')} icon={<SchoolIcon />} iconPosition="start" />
            <Tab label={t('alumni.tabs.contactAddress')} icon={<PhoneIcon />} iconPosition="start" />
            <Tab label={t('alumni.sections.addressInfo')} icon={<LocationIcon />} iconPosition="start" />
            <Tab label={t('common.info')} icon={<InfoIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Tab 0: Profile Photos */}
        <TabPanel value={tabValue} index={0}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.03), border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                p: 1.5, 
                borderRadius: 2,
                display: 'flex'
              }}>
                <PhotoLibraryIcon color="primary" fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  {t('alumni.tabs.profilePhotos')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Photos uploaded: {profilePhotos.length} / 10 (optional)
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 3 }} />

            {photoError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPhotoError(null)}>
                {photoError}
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 3 }}>
              {t('alumni.labels.setAsDefault')}
            </Alert>

            <Grid container spacing={2}>
              {profilePhotos.map((photo, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      position: 'relative', 
                      overflow: 'hidden',
                      borderRadius: 2,
                      border: photo.is_default ? `3px solid ${theme.palette.primary.main}` : 'none',
                    }}
                  >
                    <AuthenticatedAvatar
                      src={photo.url}
                      variant="square"
                      sx={{
                        width: '100%',
                        height: 300,
                        borderRadius: 0,
                      }}
                    />
                    <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleSetDefaultPhoto(index)}
                        sx={{
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'background.paper' },
                        }}
                      >
                        {photo.is_default ? <StarIcon sx={{ color: 'gold' }} /> : <StarBorderIcon />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePhoto(index)}
                        sx={{
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'error.main', color: 'white' },
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    {photo.is_default && (
                      <Chip
                        icon={<StarIcon />}
                        label="Default"
                        color="primary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                        }}
                      />
                    )}
                  </Paper>
                </Grid>
              ))}

              {profilePhotos.length < 10 && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper
                    sx={{
                      height: 300,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      border: `2px dashed ${theme.palette.divider}`,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                    onClick={() => setPhotoDialogOpen(true)}
                  >
                    <AddIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {t('common.uploadPhoto')}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Paper>
        </TabPanel>

        {/* Tab 1: Personal Information */}
        <TabPanel value={tabValue} index={1}>
          <Stack spacing={3}>
            {/* Identification Section */}
            <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.03), border: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.info.main, 0.1), 
                  p: 1.5, 
                  borderRadius: 2,
                  display: 'flex'
                }}>
                  <BadgeIcon color="info" fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="600">
                    {t('alumni.sections.identification')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('alumni.sections.personalDetails')}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('alumni.fields.studentId')} *`}
                fullWidth
                required
                value={formData.student_id}
                onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('alumni.labels.citizenId')} *`}
                fullWidth
                required
                value={formData.citizen_id}
                onChange={(e) => setFormData({ ...formData, citizen_id: e.target.value })}
              />
            </Grid>
          </Grid>
            </Paper>

            {/* Thai Name Section */}
            <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.03), border: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.1), 
                  p: 1.5, 
                  borderRadius: 2,
                  display: 'flex'
                }}>
                  <PersonIcon color="primary" fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="600">
                    {t('alumni.sections.thaiName')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('alumni.tabs.personalInfo')}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
            <Grid item xs={12} md={2}>
              <TextField
                select
                label={`${t('alumni.labels.title')} *`}
                fullWidth
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              >
                <MenuItem value="นาย">{t('alumni.titles.mr')}</MenuItem>
                <MenuItem value="นาง">{t('alumni.titles.mrs')}</MenuItem>
                <MenuItem value="นางสาว">{t('alumni.titles.miss')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                label={`${t('alumni.fields.firstNameTh')} *`}
                fullWidth
                required
                value={formData.first_name_th}
                onChange={(e) => setFormData({ ...formData, first_name_th: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                label={`${t('alumni.fields.lastNameTh')} *`}
                fullWidth
                required
                value={formData.last_name_th}
                onChange={(e) => setFormData({ ...formData, last_name_th: e.target.value })}
              />
            </Grid>
          </Grid>
            </Paper>

            {/* English Name Section */}
            <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.03), border: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.1), 
                  p: 1.5, 
                  borderRadius: 2,
                  display: 'flex'
                }}>
                  <PersonIcon color="primary" fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="600">
                    {t('alumni.sections.englishName')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('alumni.tabs.personalInfo')}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('alumni.fields.firstNameEn')} *`}
                fullWidth
                required
                value={formData.first_name_en}
                onChange={(e) => setFormData({ ...formData, first_name_en: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('alumni.fields.lastNameEn')} *`}
                fullWidth
                required
                value={formData.last_name_en}
                onChange={(e) => setFormData({ ...formData, last_name_en: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label={t('alumni.labels.nickname')}
                fullWidth
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('alumni.fields.birthDate')} *`}
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={formData.birth_date}
                onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label={`${t('alumni.fields.gender')} *`}
                fullWidth
                required
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              >
                <MenuItem value="male">{t('alumni.gender.male')}</MenuItem>
                <MenuItem value="female">{t('alumni.gender.female')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('alumni.fields.professionalLicenseNumber')}
                fullWidth
                value={formData.professional_license_number}
                onChange={(e) =>
                  setFormData({ ...formData, professional_license_number: e.target.value })
                }
              />
            </Grid>
          </Grid>
            </Paper>
          </Stack>
        </TabPanel>

        {/* Tab 2: Education */}
        <TabPanel value={tabValue} index={2}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.success.main, 0.03), border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ 
                bgcolor: alpha(theme.palette.success.main, 0.1), 
                p: 1.5, 
                borderRadius: 2,
                display: 'flex'
              }}>
                <SchoolIcon color="success" fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  {t('alumni.tabs.education')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('alumni.sections.educationInfo')}
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label={`${t('alumni.fields.degree')} *`}
                fullWidth
                required
                value={formData.degree}
                onChange={(e) => {
                  setFormData({ ...formData, degree: e.target.value });
                }}
              >
                {!degrees.some((d) => d.name_th === formData.degree || d.name_en === formData.degree) && formData.degree && (
                  <MenuItem value={formData.degree} disabled>
                    {formData.degree} (Legacy data - please update)
                  </MenuItem>
                )}
                {degrees.map((degree) => (
                  <MenuItem key={degree.code} value={degree.name_th}>
                    {degree.name_th} ({degree.name_en})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label={`${t('alumni.fields.faculty')} *`}
                fullWidth
                required
                value={formData.faculty}
                onChange={(e) => {
                  setFormData({ ...formData, faculty: e.target.value });
                }}
              >
                {!faculties.some((f) => f.name_th === formData.faculty || f.name_en === formData.faculty) && formData.faculty && (
                  <MenuItem value={formData.faculty} disabled>
                    {formData.faculty} (Legacy data - please update)
                  </MenuItem>
                )}
                {faculties.map((faculty) => (
                  <MenuItem key={faculty.code} value={faculty.name_th}>
                    {faculty.name_th} ({faculty.abbreviation_th})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label={`${t('alumni.fields.graduationYear')} *`}
                type="number"
                fullWidth
                required
                value={formData.graduation_year}
                onChange={(e) =>
                  setFormData({ ...formData, graduation_year: parseInt(e.target.value) })
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t('alumni.fields.gpa')}
                type="number"
                fullWidth
                inputProps={{ step: 0.01, min: 0, max: 4 }}
                value={formData.gpa}
                onChange={(e) => setFormData({ ...formData, gpa: e.target.value })}
              />
            </Grid>
          </Grid>
          </Paper>
        </TabPanel>

        {/* Tab 3: Contact Information */}
        <TabPanel value={tabValue} index={3}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.03), border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ 
                bgcolor: alpha(theme.palette.info.main, 0.1), 
                p: 1.5, 
                borderRadius: 2,
                display: 'flex'
              }}>
                <EmailIcon color="info" fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  {t('alumni.tabs.contactAddress')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('alumni.sections.contactInfo')}
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('alumni.fields.email')} *`}
                type="email"
                fullWidth
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('alumni.fields.phone')} *`}
                fullWidth
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('alumni.fields.lineId')}
                fullWidth
                value={formData.line_id}
                onChange={(e) => setFormData({ ...formData, line_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('alumni.fields.facebookUrl')}
                fullWidth
                value={formData.facebook}
                onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
              />
            </Grid>
          </Grid>
          </Paper>
        </TabPanel>

        {/* Tab 4: Address Information */}
        <TabPanel value={tabValue} index={4}>
          <Stack spacing={3}>
            {/* Home Address Section */}
            <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.03), border: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.1), 
                  p: 1.5, 
                  borderRadius: 2,
                  display: 'flex'
                }}>
                  <LocationIcon color="primary" fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="600">
                    {t('alumni.fields.homeAddress')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('alumni.sections.addressInfo')}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label={`${t('alumni.fields.addressLine')} *`}
                fullWidth
                required
                multiline
                rows={2}
                value={formData.address_line}
                onChange={(e) => setFormData({ ...formData, address_line: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label={`${t('alumni.fields.province')} *`}
                fullWidth
                required
                value={formData.province_code}
                onChange={(e) => handleProvinceChange(e.target.value, false)}
                disabled={geoLoading}
              >
                <MenuItem value="">{t('common.select')}</MenuItem>
                {provinces.map((province) => (
                  <MenuItem key={province.code} value={province.code.toString()}>
                    {getThaiName(province)} ({province.en_name})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label={`${t('alumni.fields.district')} *`}
                fullWidth
                required
                value={formData.district_code}
                onChange={(e) => handleDistrictChange(e.target.value, false)}
                disabled={!formData.province_code || geoLoading}
              >
                <MenuItem value="">{t('common.select')}</MenuItem>
                {homeDistricts.map((district) => (
                  <MenuItem key={district.code} value={district.code.toString()}>
                    {getThaiName(district)} ({district.en_name})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label={`${t('alumni.fields.subDistrict')} *`}
                fullWidth
                required
                value={formData.sub_district_code}
                onChange={(e) => handleSubDistrictChange(e.target.value, false)}
                disabled={!formData.district_code || geoLoading}
              >
                <MenuItem value="">{t('common.select')}</MenuItem>
                {homeSubDistricts.map((subDistrict) => (
                  <MenuItem key={subDistrict.code} value={subDistrict.code.toString()}>
                    {getThaiName(subDistrict)} ({subDistrict.en_name})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('alumni.fields.postalCode')} *`}
                fullWidth
                required
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={`${t('alumni.fields.country')} *`}
                fullWidth
                required
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </Grid>
          </Grid>
            </Paper>

            {/* Work Address Section */}
            <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.warning.main, 0.03), border: 1, borderColor: 'divider' }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Box sx={{ 
                  bgcolor: alpha(theme.palette.warning.main, 0.1), 
                  p: 1.5, 
                  borderRadius: 2,
                  display: 'flex'
                }}>
                  <WorkIcon color="warning" fontSize="large" />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight="600">
                    {t('alumni.fields.workAddressOptional')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('alumni.sections.workAddressInfo')}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                label={t('alumni.fields.addressLine')}
                fullWidth
                multiline
                rows={2}
                value={formData.work_address_line}
                onChange={(e) => setFormData({ ...formData, work_address_line: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label={t('alumni.fields.province')}
                fullWidth
                value={formData.work_province_code}
                onChange={(e) => handleProvinceChange(e.target.value, true)}
                disabled={geoLoading}
              >
                <MenuItem value="">{t('common.select')}</MenuItem>
                {provinces.map((province) => (
                  <MenuItem key={province.code} value={province.code.toString()}>
                    {getThaiName(province)} ({province.en_name})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label={t('alumni.fields.district')}
                fullWidth
                value={formData.work_district_code}
                onChange={(e) => handleDistrictChange(e.target.value, true)}
                disabled={!formData.work_province_code || geoLoading}
              >
                <MenuItem value="">{t('common.select')}</MenuItem>
                {workDistricts.map((district) => (
                  <MenuItem key={district.code} value={district.code.toString()}>
                    {getThaiName(district)} ({district.en_name})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                label={t('alumni.fields.subDistrict')}
                fullWidth
                value={formData.work_sub_district_code}
                onChange={(e) => handleSubDistrictChange(e.target.value, true)}
                disabled={!formData.work_district_code || geoLoading}
              >
                <MenuItem value="">{t('common.select')}</MenuItem>
                {workSubDistricts.map((subDistrict) => (
                  <MenuItem key={subDistrict.code} value={subDistrict.code.toString()}>
                    {getThaiName(subDistrict)} ({subDistrict.en_name})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('alumni.fields.postalCode')}
                fullWidth
                value={formData.work_postal_code}
                onChange={(e) => setFormData({ ...formData, work_postal_code: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('alumni.fields.country')}
                fullWidth
                value={formData.work_country}
                onChange={(e) => setFormData({ ...formData, work_country: e.target.value })}
              />
            </Grid>
          </Grid>
            </Paper>
          </Stack>
        </TabPanel>

        {/* Tab 5: Additional Information */}
        <TabPanel value={tabValue} index={5}>
          <Paper elevation={0} sx={{ p: 3, bgcolor: alpha(theme.palette.secondary.main, 0.03), border: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Box sx={{ 
                bgcolor: alpha(theme.palette.secondary.main, 0.1), 
                p: 1.5, 
                borderRadius: 2,
                display: 'flex'
              }}>
                <InfoIcon color="secondary" fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="600">
                  {t('common.info')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('alumni.sections.professionalInfo')}
                </Typography>
              </Box>
            </Stack>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label={`${t('alumni.fields.alumniType')} *`}
                fullWidth
                required
                value={formData.alumni_type}
                onChange={(e) => setFormData({ ...formData, alumni_type: e.target.value })}
              >
                <MenuItem value="regular">{t('alumni.type.regular')}</MenuItem>
                <MenuItem value="honorary">{t('alumni.type.honorary')}</MenuItem>
                <MenuItem value="distinguished">{t('alumni.type.distinguished')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('alumni.fields.batch')}
                fullWidth
                value={formData.batch}
                onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('alumni.fields.currentOccupation')}
                fullWidth
                value={formData.current_occupation}
                onChange={(e) => setFormData({ ...formData, current_occupation: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('alumni.fields.ideals')}
                fullWidth
                multiline
                rows={3}
                placeholder="e.g., honesty, integrity, excellence"
                value={formData.ideals}
                onChange={(e) => setFormData({ ...formData, ideals: e.target.value })}
                helperText="Separate multiple ideals with commas"
              />
            </Grid>
          </Grid>
          </Paper>
        </TabPanel>
      </Card>

      {/* Photo Upload Dialog */}
      <Dialog open={photoDialogOpen} onClose={() => { setPhotoDialogOpen(false); stopCamera(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add Profile Photo</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {photoError && <Alert severity="error">{photoError}</Alert>}
            {!cameraActive ? (
              <>
                <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  Upload from Device
                </Button>
                <Button variant="outlined" startIcon={<CameraAltIcon />} onClick={startCamera}>
                  Take Photo
                </Button>
                <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileUpload} />
              </>
            ) : (
              <>
                <Box sx={{ position: 'relative', width: '100%', paddingTop: '75%', backgroundColor: '#000', borderRadius: '8px', overflow: 'hidden' }}>
                  <video 
                    ref={videoRef} 
                    autoPlay 
                    playsInline
                    muted
                    style={{ 
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%', 
                      height: '100%',
                      objectFit: 'cover'
                    }} 
                  />
                </Box>
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <Stack direction="row" spacing={2}>
                  <Button variant="contained" startIcon={<PhotoCameraIcon />} onClick={capturePhoto} fullWidth>
                    Capture
                  </Button>
                  <Button variant="outlined" onClick={stopCamera} fullWidth>
                    Cancel
                  </Button>
                </Stack>
              </>
            )}
            {uploading && <CircularProgress />}
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default AlumniEdit;
